/**
 * @file BuilderPhone.tsx
 * @description Конструктор сайта на телефоне (< 768; phase19.16 Р1-6а;
 * uiux-builder-shell.md §2.3): вкладки «Страница · Весь сайт · Просмотр».
 *  - «Страница» — переключатель страниц, подсказка, структура;
 *  - «Весь сайт» — разделы строками 64 с пояснениями;
 *  - «Просмотр» — канва (iframe грузится только здесь); выбранный на канве
 *    блок открывает немодальную шторку на 55 % высоты — сайт над ней живой
 *    и занимает всё место над шторкой.
 * Выбор из списков открывает панель на весь экран: одна «←», внизу
 * [Смотреть][Опубликовать] (у блока — его «Сохранить» и «Смотреть» сверху).
 * SB125: плашка «Сайт работает без: …» — под вкладками «Страница» и «Весь
 * сайт» (на «Просмотре» её показывают полосы над канвой).
 */

import { Eye } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Tabs } from "../ui/Tabs";
import { useBuilder } from "./builder-context";
import { BuilderNotices } from "./BuilderNotices";
import { LiveChecksNotice } from "../site-view/LiveChecksNotice";
import { BuilderCanvas } from "./canvas/BuilderCanvas";
import { BuilderNav } from "./nav/BuilderNav";
import { BuilderPanel } from "./panels/BuilderPanel";
import { PhonePanelBar } from "./top/PhonePanelBar";
import { settingsButtonLabel } from "../../lib/site-builder/panels";

type PhoneTab = "page" | "site" | "view";

const TABS = [
  { value: "page" as const, label: "Страница" },
  { value: "site" as const, label: "Весь сайт" },
  { value: "view" as const, label: "Просмотр" },
];

export function BuilderPhone() {
  const b = useBuilder();
  const { selection } = b;
  // Открыли ссылку на раздел — «Весь сайт»; на блок или шапку — «Просмотр» со шторкой.
  const [tab, setTab] = useState<PhoneTab>(
    selection.panel && selection.panel !== "pages" ? "site" : selection.sel.kind !== "page" ? "view" : "page",
  );
  // Пустой выбор на странице — панели нет (настройки страницы — кнопкой).
  const [pageSettings, setPageSettings] = useState(false);
  const hasSelection = (selection.panel !== null && selection.panel !== "pages") || selection.sel.kind !== "page";
  const panelOpen = hasSelection || pageSettings;
  const sheetOpen = panelOpen && tab === "view";
  const isBlock = !selection.panel && selection.sel.kind === "block";

  useEffect(() => {
    if (!hasSelection) return;
    setPageSettings(false);
  }, [hasSelection]);

  function close() {
    setPageSettings(false);
    void b.select(selection.panel ? { panel: null } : { sel: { kind: "page" } });
  }

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      {/* Со шторкой вкладки закреплены над сайтом — переключиться можно, не закрывая её. */}
      <div className={sheetOpen ? "fixed inset-x-0 top-[var(--shell-sticky-top)] z-40 bg-background px-4" : "sticky top-[var(--shell-sticky-top)] z-10 shrink-0 bg-background"}>
        <Tabs aria-label="Конструктор" items={TABS} value={tab} onChange={setTab} />
      </div>
      {/* SB125: «Сайт работает без: …» видна на всех вкладках; на «Просмотре» — в полосах над канвой. */}
      {tab === "view" ? null : <LiveChecksNotice />}
      {tab === "page" ? (
        <>
          <Button variant="outline" className="self-start" onClick={() => setPageSettings(true)}>
            {settingsButtonLabel(selection.screen)}
          </Button>
          <BuilderNav part="page" />
        </>
      ) : null}
      {tab === "site" ? <BuilderNav part="site" /> : null}
      {tab === "view" ? (
        // Со шторкой сайт занимает всё место над ней (иначе шапка и вкладки съедают
        // его целиком на 360). Тот же элемент — iframe не перезагружается.
        <div className={sheetOpen ? "fixed inset-x-0 bottom-[55dvh] top-[calc(var(--shell-sticky-top)+3.25rem)] z-30 flex flex-col bg-background p-2" : "flex h-[calc(100dvh-var(--shell-sticky-top)-13rem)] min-h-96 flex-col gap-2"}>
          {sheetOpen ? null : <BuilderNotices />}
          <BuilderCanvas className="flex-1" toolbar={!sheetOpen} />
        </div>
      ) : null}

      {sheetOpen ? (
        // Немодальная шторка: сайт над ней остаётся живым (перекраска видна). Слой — как у
        // шторок кабинета (--z-dialog): кнопка чата не ложится на «Сохранить» панели.
        <div role="region" aria-label="Настройки выбранного" className="fixed inset-x-0 bottom-0 z-[var(--z-dialog)] flex h-[55dvh] flex-col rounded-t-2xl bg-surface-lowest p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] [box-shadow:0_-8px_24px_var(--shadow-ambient)]">
          <BuilderPanel onClose={close} className="flex-1" />
        </div>
      ) : null}
      {panelOpen && tab !== "view" ? (
        <div role="dialog" aria-modal="true" aria-label="Настройки" className="fixed inset-x-0 bottom-0 top-[var(--shell-sticky-top)] z-[var(--z-dialog)] flex flex-col bg-background px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          {/* Одна «←»; у блока внизу его «Сохранить», у разделов — [Смотреть][Опубликовать]. */}
          <BuilderPanel
            onBack={close}
            className="min-h-0 flex-1"
            headerExtra={
              isBlock ? (
                <Button variant="outline" size="icon" className="size-12 shrink-0" aria-label="Смотреть на сайте" onClick={() => setTab("view")}>
                  <Eye aria-hidden />
                </Button>
              ) : null
            }
          />
          {isBlock ? null : <PhonePanelBar onView={() => setTab("view")} />}
        </div>
      ) : null}
    </div>
  );
}
